import { useTheme } from '@automata/mui-theme';
import { Redo as RedoIcon, Undo as UndoIcon } from '@automata/mui-theme/icons';
import { logger } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ToggleButtonGroup, {
  ToggleButtonGroupProps,
} from '@mui/material/ToggleButtonGroup';
import React from 'react';
import { ActionCreators as ReduxUndoActionCreators } from 'redux-undo';

import { AutoSaveToggleButton } from './AutoSaveToggleButton';
import { UndoRedoButton } from './UndoRedoButton';
import {
  selectRedoHasFuture,
  selectUndoHasHistory,
} from '../../../features/selectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

type UndoRedoToolbarProps = {
  workflowId?: string;
  isAutoSaveEnabled: boolean;
  setIsAutoSaveEnabled: (value: boolean) => void;
};

export const UndoRedoToolbar = ({
  workflowId,
  isAutoSaveEnabled,
  setIsAutoSaveEnabled,
}: UndoRedoToolbarProps) => {
  const undoEnabled = useAppSelector(selectUndoHasHistory);
  const redoEnabled = useAppSelector(selectRedoHasFuture);
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isAutoSaveFeatureEnabled = useFlag('fe.canvas_autosave');

  const handleToggleButtonGroupChange: ToggleButtonGroupProps['onChange'] = (
    _,
    value: 'undo' | 'redo'
  ) => {
    if (value === 'undo') {
      dispatch(ReduxUndoActionCreators.undo());
    } else if (value === 'redo') {
      dispatch(ReduxUndoActionCreators.redo());
    } else {
      logger.error('Unknown value:', value);
    }
  };

  return (
    <Box boxShadow={3} borderRadius={1} bgcolor={theme.palette.common.white}>
      <ToggleButtonGroup
        orientation="vertical"
        onChange={handleToggleButtonGroupChange}
        exclusive
        size="medium"
      >
        <UndoRedoButton
          title="Undo"
          value="undo"
          disabled={!undoEnabled}
          icon={UndoIcon}
        />
        <UndoRedoButton
          title="Redo"
          value="redo"
          disabled={!redoEnabled}
          icon={RedoIcon}
        />
        {workflowId && isAutoSaveFeatureEnabled && (
          <>
            <Divider />
            <AutoSaveToggleButton
              enabled={isAutoSaveEnabled}
              onChange={setIsAutoSaveEnabled}
            />
          </>
        )}
      </ToggleButtonGroup>
    </Box>
  );
};
